import React, { useEffect, useState } from 'react';
import { createSaleMandate, updateSaleMandate, updateMandateTeamContacts } from '../../api/saleMandates';
import { useTeamMembers } from '../../hooks/useTeamMembers';
import { apiClient } from '../../api/client';
import { SaleMandate } from '../../types';
import { AssetClassPicker, PickerAssetClass } from '../shared/AssetClassPicker';
import { FieldGroup } from '../ui/FieldGroup';
import { COUNTRIES as COUNTRY_LIST } from '../../lib/countries';

interface Props {
  initial?: SaleMandate;
  onSuccess: () => void;
  onCancel: () => void;
}

const COUNTRIES = COUNTRY_LIST;

const RISK_OPTIONS = [
  { value: '', label: '— wählen —' },
  { value: 'core', label: 'Core' },
  { value: 'core_plus', label: 'Core+' },
  { value: 'value_add', label: 'Value Add' },
  { value: 'opportunistic', label: 'Opportunistisch' },
];

const STRUCTURES = ['Asset Deal', 'Share Deal', 'Forward Purchase', 'Forward Funding', 'Portfolio'];

const PROZESS_PHASEN = [
  '', 'Teaser versandt', 'NDA-Phase', 'Investment Memorandum',
  'Datenraum offen', 'Indikative Angebote', 'Shortlist', 'Due Diligence',
  'Finale Angebote', 'Exklusivität', 'Signing', 'Closing',
];

function MultiBtnSelect({ options, selected, onChange }:
  { options: string[]; selected: string[]; onChange: (v: string[]) => void }) {
  const toggle = (o: string) => onChange(selected.includes(o) ? selected.filter(x => x !== o) : [...selected, o]);
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
      {options.map(o => {
        const active = selected.includes(o);
        return (
          <button key={o} type="button" onClick={() => toggle(o)} style={{
            padding: '3px 10px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
            background: active ? 'var(--accent)' : 'var(--surface2)',
            color: active ? '#fff' : 'var(--text2)',
            border: active ? 'none' : '1px solid var(--border)',
            fontWeight: active ? 500 : 400,
          }}>{o}</button>
        );
      })}
    </div>
  );
}

type FormTab = 'grunddaten' | 'kennzahlen' | 'teaser' | 'prozess';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid var(--border2)',
  borderRadius: 'var(--radius)', fontSize: '13px', background: 'var(--surface)',
  color: 'var(--text)', outline: 'none',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '11px', fontWeight: 500, textTransform: 'uppercase',
  letterSpacing: '0.5px', color: 'var(--text3)', marginBottom: '5px',
};
const sectionHead: React.CSSProperties = {
  fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px',
  color: 'var(--accent)', marginBottom: '10px', marginTop: '4px',
  paddingBottom: '6px', borderBottom: '1px solid var(--accent-light)',
};
const textareaStyle: React.CSSProperties = {
  ...inputStyle, minHeight: '90px', resize: 'vertical', lineHeight: '1.5',
};

export function SaleMandateForm({ initial, onSuccess, onCancel }: Props) {
  const isEdit = !!initial;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<FormTab>('grunddaten');

  const { data: teamMembers = [] } = useTeamMembers();

  const initialPriceMio = initial?.asking_price_eur != null
    ? String(initial.asking_price_eur / 1_000_000)
    : '';

  const [form, setForm] = useState<Record<string, string>>({
    title: initial?.title ?? '',
    project_name: initial?.project_name ?? '',
    street: initial?.street ?? '',
    city: initial?.city ?? '',
    country_code: initial?.country_code ?? 'DE',
    size_sqm: initial?.size_sqm != null ? String(initial.size_sqm) : '',
    asking_price_mio: initialPriceMio,
    description: initial?.description ?? '',
    status: initial?.status ?? 'active',
    risk_profile: initial?.risk_profile ?? '',
    transaction_structure: initial?.transaction_structure ?? '',
    // Kennzahlen
    baujahr: initial?.baujahr != null ? String(initial.baujahr) : '',
    modernisierung: initial?.modernisierung ?? '',
    grundstueck_sqm: initial?.grundstueck_sqm != null ? String(initial.grundstueck_sqm) : '',
    stellplaetze: initial?.stellplaetze != null ? String(initial.stellplaetze) : '',
    anzahl_mieter: initial?.anzahl_mieter != null ? String(initial.anzahl_mieter) : '',
    vermietungsstand_pct: initial?.vermietungsstand_pct != null ? String(initial.vermietungsstand_pct) : '',
    walt_years: initial?.walt_years != null ? String(initial.walt_years) : '',
    ist_nettomiete_mio: initial?.ist_nettomiete_eur != null ? String(initial.ist_nettomiete_eur / 1_000_000) : '',
    soll_nettomiete_mio: initial?.soll_nettomiete_eur != null ? String(initial.soll_nettomiete_eur / 1_000_000) : '',
    noi_mio: initial?.noi_eur != null ? String(initial.noi_eur / 1_000_000) : '',
    leerstand_pct: initial?.leerstand_pct != null ? String(initial.leerstand_pct) : '',
    mietindexierung: initial?.mietindexierung ?? '',
    capex_kurzfristig: initial?.capex_kurzfristig ?? '',
    energieausweis: initial?.energieausweis ?? '',
    // Teaser-Texte
    executive_summary: initial?.executive_summary ?? '',
    investment_story: initial?.investment_story ?? '',
    standort_makro: initial?.standort_makro ?? '',
    standort_mikro: initial?.standort_mikro ?? '',
    standort_markt: initial?.standort_markt ?? '',
    gebaeude_beschreibung: initial?.gebaeude_beschreibung ?? '',
    mieterstruktur_text: initial?.mieterstruktur_text ?? '',
    ertragssituation_text: initial?.ertragssituation_text ?? '',
    value_creation_text: initial?.value_creation_text ?? '',
    esg_text: initial?.esg_text ?? '',
    // Prozess
    verkaufsprozess_text: initial?.verkaufsprozess_text ?? '',
    vertraulichkeitshinweis: initial?.vertraulichkeitshinweis ?? '',
    prozess_phase: initial?.prozess_phase ?? '',
  });

  // PFLICHT — pro Mandat genau 1 Assetklasse (Konzept §4.4)
  const [assetClassId, setAssetClassId] = useState<string | null>(initial?.asset_class_id ?? null);
  const [assetClassLabel, setAssetClassLabel] = useState<string>(initial?.asset_class_label ?? '');
  const [classSchema, setClassSchema] = useState<PickerAssetClass['field_schema']>(undefined);

  // Bereich A — suchrelevante Multi-Select-Felder (Konzept §3.3)
  const [criteria, setCriteria] = useState<Record<string, string[]>>({});
  useEffect(() => {
    apiClient.get<{ data: Record<string, string[]> }>('/asset-classes/criterion-options')
      .then(r => setCriteria(r.data.data ?? {})).catch(() => {});
  }, []);
  // Im Edit-Modus: field_schema der initial-Klasse laden für reorderForClass
  useEffect(() => {
    if (initial?.asset_class_id) {
      apiClient.get<{ data: unknown; flat: PickerAssetClass[] }>('/asset-classes')
        .then(r => {
          const ac = (r.data.flat ?? []).find(c => c.id === initial.asset_class_id);
          if (ac) setClassSchema(ac.field_schema);
        }).catch(() => {});
    }
  }, [initial?.asset_class_id]);
  type SuggestedKey = keyof NonNullable<NonNullable<PickerAssetClass['field_schema']>['suggested']>;
  const reorderForClass = (pool: string[], category: SuggestedKey): string[] => {
    const sug = classSchema?.suggested?.[category] ?? [];
    if (!sug.length) return pool;
    const inPool = sug.filter(s => pool.includes(s));
    const extras = sug.filter(s => !pool.includes(s));
    const rest   = pool.filter(p => !sug.includes(p));
    return [...inPool, ...extras, ...rest];
  };
  /** Bei Klassen-Wechsel im Mandat: Multi-Selects der Such-Felder mit Suggestions vorbelegen */
  const handleAssetClassChange = (id: string, label: string, ac?: PickerAssetClass) => {
    const sug = ac?.field_schema?.suggested ?? {};
    setAssetClassId(id);
    setAssetClassLabel(label);
    setClassSchema(ac?.field_schema);
    // Mandat-Titel: leer ODER war alter Klassen-Label → mit neuem überschreiben
    if (!form.title.trim() || form.title.trim() === assetClassLabel.trim()) {
      set('title', label);
    }
    // Such-Multi-Selects nur überschreiben, wenn aktuell exakt der vorigen Klasse entsprechend (oder leer)
    setLocationProfile(sug.standortprofil ?? []);
    setTenantsOperators(sug.mieter_betreiber ?? []);
    setEsgLabels(sug.esg_energie ?? []);
    setDealStructures(sug.deal_struktur ?? []);
  };
  const [locationProfile, setLocationProfile]   = useState<string[]>(initial?.location_profile ?? []);
  const [tenantsOperators, setTenantsOperators] = useState<string[]>(initial?.tenants_operators ?? []);
  const [esgLabels, setEsgLabels]               = useState<string[]>(initial?.esg_labels ?? []);
  const [dealStructures, setDealStructures]     = useState<string[]>(initial?.deal_structures ?? []);

  const [highlights, setHighlights] = useState<string[]>(initial?.investment_highlights ?? []);
  const [selectedTeam, setSelectedTeam] = useState<string[]>(
    initial?.team_contacts?.map(tc => tc.id) ?? []
  );

  const set = (field: string, value: string) => setForm(f => ({ ...f, [field]: value }));

  const numOrNull = (v: string) => v ? parseFloat(v) : null;
  const intOrNull = (v: string) => v ? parseInt(v) : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Bezeichnung ist Pflichtfeld'); return; }
    if (!assetClassId) { setError('Assetklasse ist Pflicht — bitte oben wählen.'); setActiveTab('grunddaten'); return; }
    setLoading(true);
    setError(null);
    try {
      const priceMio = form.asking_price_mio ? parseFloat(form.asking_price_mio) : null;
      const payload: Partial<SaleMandate> = {
        title: form.title.trim(),
        project_name: form.project_name.trim() || undefined,
        street: form.street.trim() || undefined,
        city: form.city.trim() || undefined,
        country_code: form.country_code || undefined,
        size_sqm: numOrNull(form.size_sqm) ?? undefined,
        asking_price_eur: priceMio != null ? priceMio * 1_000_000 : undefined,
        description: form.description.trim() || undefined,
        status: form.status,
        asset_class_id: assetClassId ?? undefined,
        asset_class_source: 'user',
        risk_profile: form.risk_profile || undefined,
        transaction_structure: form.transaction_structure || undefined,
        // Bereich A — suchrelevante Single-Value-Felder
        volume_eur: priceMio != null ? priceMio * 1_000_000 : undefined,
        size_sqm_total: numOrNull(form.size_sqm) ?? undefined,
        units: intOrNull(form.anzahl_mieter) ?? undefined,
        year_built_match: intOrNull(form.baujahr) ?? undefined,
        risk_class: form.risk_profile || undefined,
        location_profile: locationProfile.length ? locationProfile : undefined,
        tenants_operators: tenantsOperators.length ? tenantsOperators : undefined,
        esg_labels: esgLabels.length ? esgLabels : undefined,
        deal_structures: dealStructures.length ? dealStructures : undefined,
        // Kennzahlen
        baujahr: intOrNull(form.baujahr) ?? undefined,
        modernisierung: form.modernisierung.trim() || undefined,
        grundstueck_sqm: numOrNull(form.grundstueck_sqm) ?? undefined,
        stellplaetze: intOrNull(form.stellplaetze) ?? undefined,
        anzahl_mieter: intOrNull(form.anzahl_mieter) ?? undefined,
        vermietungsstand_pct: numOrNull(form.vermietungsstand_pct) ?? undefined,
        walt_years: numOrNull(form.walt_years) ?? undefined,
        ist_nettomiete_eur: form.ist_nettomiete_mio ? parseFloat(form.ist_nettomiete_mio) * 1_000_000 : undefined,
        soll_nettomiete_eur: form.soll_nettomiete_mio ? parseFloat(form.soll_nettomiete_mio) * 1_000_000 : undefined,
        noi_eur: form.noi_mio ? parseFloat(form.noi_mio) * 1_000_000 : undefined,
        leerstand_pct: numOrNull(form.leerstand_pct) ?? undefined,
        mietindexierung: form.mietindexierung.trim() || undefined,
        capex_kurzfristig: form.capex_kurzfristig.trim() || undefined,
        energieausweis: form.energieausweis.trim() || undefined,
        // Teaser
        executive_summary: form.executive_summary.trim() || undefined,
        investment_highlights: highlights.filter(h => h.trim()) || undefined,
        investment_story: form.investment_story.trim() || undefined,
        standort_makro: form.standort_makro.trim() || undefined,
        standort_mikro: form.standort_mikro.trim() || undefined,
        standort_markt: form.standort_markt.trim() || undefined,
        gebaeude_beschreibung: form.gebaeude_beschreibung.trim() || undefined,
        mieterstruktur_text: form.mieterstruktur_text.trim() || undefined,
        ertragssituation_text: form.ertragssituation_text.trim() || undefined,
        value_creation_text: form.value_creation_text.trim() || undefined,
        esg_text: form.esg_text.trim() || undefined,
        // Prozess
        verkaufsprozess_text: form.verkaufsprozess_text.trim() || undefined,
        vertraulichkeitshinweis: form.vertraulichkeitshinweis.trim() || undefined,
        prozess_phase: form.prozess_phase || undefined,
      };

      let mandateId: string;
      if (isEdit && initial) {
        await updateSaleMandate(initial.id, payload);
        mandateId = initial.id;
      } else {
        const result = await createSaleMandate(payload);
        mandateId = result.data.id;
      }

      // Team-Kontakte zuweisen
      if (selectedTeam.length > 0 || (isEdit && initial?.team_contacts?.length)) {
        await updateMandateTeamContacts(mandateId, selectedTeam);
      }

      onSuccess();
    } catch (err: unknown) {
      const axiosMsg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setError(axiosMsg ?? (err instanceof Error ? err.message : 'Unbekannter Fehler'));
    } finally {
      setLoading(false);
    }
  };

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 16px', fontSize: '12px', fontWeight: active ? 600 : 400,
    color: active ? 'var(--accent)' : 'var(--text3)',
    background: 'none', border: 'none', cursor: 'pointer',
    borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
  });

  return (
    <form onSubmit={handleSubmit}>
      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: '16px', marginTop: '-4px', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex' }}>
          <button type="button" style={tabStyle(activeTab === 'grunddaten')} onClick={() => setActiveTab('grunddaten')}>
            Grunddaten <span style={{ fontSize: 9, color: '#2a7a4b', fontWeight: 600, marginLeft: 4 }}>· SUCHE</span>
          </button>
          <button type="button" style={tabStyle(activeTab === 'kennzahlen')} onClick={() => setActiveTab('kennzahlen')}>
            Kennzahlen <span style={{ fontSize: 9, color: '#7a7466', fontWeight: 600, marginLeft: 4 }}>· EXPOSÉ</span>
          </button>
          <button type="button" style={tabStyle(activeTab === 'teaser')} onClick={() => setActiveTab('teaser')}>
            Teaser-Texte <span style={{ fontSize: 9, color: '#7a7466', fontWeight: 600, marginLeft: 4 }}>· EXPOSÉ</span>
          </button>
          <button type="button" style={tabStyle(activeTab === 'prozess')} onClick={() => setActiveTab('prozess')}>
            Prozess <span style={{ fontSize: 9, color: '#7a7466', fontWeight: 600, marginLeft: 4 }}>· EXPOSÉ</span>
          </button>
        </div>
        {assetClassLabel && (
          <div style={{ padding: '3px 10px', background: 'var(--accent)', color: '#fff', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
            {assetClassLabel}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gap: '14px' }}>

        {/* ── TAB: GRUNDDATEN ── */}
        {activeTab === 'grunddaten' && (
          <>
            <div style={sectionHead}>Objekt</div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Bezeichnung *</label>
                <input style={inputStyle} value={form.title}
                  onChange={e => set('title', e.target.value)}
                  placeholder="z.B. Bürogebäude Frankfurt Innenstadt"
                  required autoFocus />
              </div>
              <div>
                <label style={labelStyle}>Projektname</label>
                <input style={inputStyle} value={form.project_name}
                  onChange={e => set('project_name', e.target.value)}
                  placeholder="z.B. Project Atlas" />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Status</label>
              <select style={inputStyle} value={form.status}
                onChange={e => set('status', e.target.value)}>
                <option value="active">Aktiv</option>
                <option value="sold">Verkauft</option>
                <option value="withdrawn">Zurückgezogen</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>Straße / Adresse</label>
              <input style={inputStyle} value={form.street}
                onChange={e => set('street', e.target.value)}
                placeholder="z.B. Mainzer Landstr. 50" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Stadt</label>
                <input style={inputStyle} value={form.city}
                  onChange={e => set('city', e.target.value)}
                  placeholder="z.B. Frankfurt" />
              </div>
              <div>
                <label style={labelStyle}>Land</label>
                <select style={inputStyle} value={form.country_code}
                  onChange={e => set('country_code', e.target.value)}>
                  {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label style={labelStyle}>Beschreibung</label>
              <textarea style={textareaStyle}
                value={form.description}
                onChange={e => set('description', e.target.value)}
                placeholder="Kurze Objektbeschreibung..." />
            </div>

            <div style={{ ...sectionHead, marginTop: '8px' }}>Klassifikation & Suchkriterien</div>

            <FieldGroup label="① Assetklasse (Pflicht — bestimmt das Matching)" tone="accent">
              {isEdit && assetClassLabel ? (
                <div style={{ padding: '8px 12px', background: 'var(--surface)', borderRadius: 'var(--radius)', fontSize: 13 }}>
                  {assetClassLabel} <span style={{ color: 'var(--text3)', fontSize: 11 }}>(nicht änderbar)</span>
                </div>
              ) : (
                <AssetClassPicker
                  value={assetClassId}
                  onChange={(id, label, ac) => handleAssetClassChange(id, label, ac)}
                  required
                />
              )}
            </FieldGroup>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Risikoprofil</label>
                <select style={inputStyle} value={form.risk_profile}
                  onChange={e => set('risk_profile', e.target.value)}>
                  {RISK_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Transaktionsstruktur</label>
                <select style={inputStyle} value={form.transaction_structure}
                  onChange={e => set('transaction_structure', e.target.value)}>
                  <option value="">— wählen —</option>
                  {STRUCTURES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* Bereich A — Multi-Select aus DB-Optionen, klassenabhängig sortiert */}
            <FieldGroup label="Standortprofil (Suche)" tone="default" hint={assetClassLabel ? `Vorschläge für ${assetClassLabel}` : undefined}>
              <MultiBtnSelect options={reorderForClass(criteria.standortprofil ?? [], 'standortprofil')} selected={locationProfile} onChange={setLocationProfile} />
            </FieldGroup>
            <FieldGroup label="Mieter / Betreiber (Suche)" tone="default">
              <MultiBtnSelect options={reorderForClass(criteria.mieter_betreiber ?? [], 'mieter_betreiber')} selected={tenantsOperators} onChange={setTenantsOperators} />
            </FieldGroup>
            <FieldGroup label="ESG / Energie (Suche)" tone="default">
              <MultiBtnSelect options={reorderForClass(criteria.esg_energie ?? [], 'esg_energie')} selected={esgLabels} onChange={setEsgLabels} />
            </FieldGroup>
            <FieldGroup label="Deal-Struktur (Suche)" tone="default">
              <MultiBtnSelect options={reorderForClass(criteria.deal_struktur ?? [], 'deal_struktur')} selected={dealStructures} onChange={setDealStructures} />
            </FieldGroup>
          </>
        )}

        {/* ── TAB: KENNZAHLEN ── */}
        {activeTab === 'kennzahlen' && (
          <>
            <div style={sectionHead}>Flächen & Gebäude</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Mietfläche (m²)</label>
                <input style={inputStyle} type="number" value={form.size_sqm}
                  onChange={e => set('size_sqm', e.target.value)} placeholder="z.B. 24500" min="0" />
              </div>
              <div>
                <label style={labelStyle}>Grundstück (m²)</label>
                <input style={inputStyle} type="number" value={form.grundstueck_sqm}
                  onChange={e => set('grundstueck_sqm', e.target.value)} placeholder="z.B. 8900" min="0" />
              </div>
              <div>
                <label style={labelStyle}>Stellplätze</label>
                <input style={inputStyle} type="number" value={form.stellplaetze}
                  onChange={e => set('stellplaetze', e.target.value)} placeholder="z.B. 280" min="0" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Baujahr</label>
                <input style={inputStyle} type="number" value={form.baujahr}
                  onChange={e => set('baujahr', e.target.value)} placeholder="z.B. 2012" />
              </div>
              <div>
                <label style={labelStyle}>Modernisierung</label>
                <input style={inputStyle} value={form.modernisierung}
                  onChange={e => set('modernisierung', e.target.value)} placeholder="z.B. 2020 kernsaniert" />
              </div>
              <div>
                <label style={labelStyle}>Energieausweis</label>
                <input style={inputStyle} value={form.energieausweis}
                  onChange={e => set('energieausweis', e.target.value)} placeholder="z.B. A+" />
              </div>
            </div>

            <div style={{ ...sectionHead, marginTop: '8px' }}>Finanzielle Kennzahlen</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Kaufpreis (Mio. €)</label>
                <input style={inputStyle} type="number" value={form.asking_price_mio}
                  onChange={e => set('asking_price_mio', e.target.value)} placeholder="z.B. 100" min="0" step="0.01" />
              </div>
              <div>
                <label style={labelStyle}>Ist-Nettomiete (Mio. €/a)</label>
                <input style={inputStyle} type="number" value={form.ist_nettomiete_mio}
                  onChange={e => set('ist_nettomiete_mio', e.target.value)} placeholder="z.B. 5.4" min="0" step="0.01" />
              </div>
              <div>
                <label style={labelStyle}>Soll-Nettomiete (Mio. €/a)</label>
                <input style={inputStyle} type="number" value={form.soll_nettomiete_mio}
                  onChange={e => set('soll_nettomiete_mio', e.target.value)} placeholder="z.B. 5.8" min="0" step="0.01" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>NOI (Mio. €/a)</label>
                <input style={inputStyle} type="number" value={form.noi_mio}
                  onChange={e => set('noi_mio', e.target.value)} placeholder="z.B. 5.0" min="0" step="0.01" />
              </div>
              <div>
                <label style={labelStyle}>Mietindexierung</label>
                <input style={inputStyle} value={form.mietindexierung}
                  onChange={e => set('mietindexierung', e.target.value)} placeholder="z.B. teilweise, VPI" />
              </div>
              <div>
                <label style={labelStyle}>CapEx kurzfristig</label>
                <input style={inputStyle} value={form.capex_kurzfristig}
                  onChange={e => set('capex_kurzfristig', e.target.value)} placeholder="z.B. gering" />
              </div>
            </div>

            <div style={{ ...sectionHead, marginTop: '8px' }}>Vermietung</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Vermietungsquote (%)</label>
                <input style={inputStyle} type="number" value={form.vermietungsstand_pct}
                  onChange={e => set('vermietungsstand_pct', e.target.value)} placeholder="z.B. 94" min="0" max="100" step="0.1" />
              </div>
              <div>
                <label style={labelStyle}>Leerstand (%)</label>
                <input style={inputStyle} type="number" value={form.leerstand_pct}
                  onChange={e => set('leerstand_pct', e.target.value)} placeholder="z.B. 6" min="0" max="100" step="0.1" />
              </div>
              <div>
                <label style={labelStyle}>WALT (Jahre)</label>
                <input style={inputStyle} type="number" value={form.walt_years}
                  onChange={e => set('walt_years', e.target.value)} placeholder="z.B. 5.8" min="0" step="0.1" />
              </div>
              <div>
                <label style={labelStyle}>Anzahl Mieter</label>
                <input style={inputStyle} type="number" value={form.anzahl_mieter}
                  onChange={e => set('anzahl_mieter', e.target.value)} placeholder="z.B. 11" min="0" />
              </div>
            </div>
          </>
        )}

        {/* ── TAB: TEASER-TEXTE ── */}
        {activeTab === 'teaser' && (
          <>
            <div style={sectionHead}>Executive Summary</div>
            <textarea style={{ ...textareaStyle, minHeight: '100px' }}
              value={form.executive_summary}
              onChange={e => set('executive_summary', e.target.value)}
              placeholder="Ein institutionell geeignetes Investment mit stabiler Ertragssituation..." />

            <div style={sectionHead}>Investment Highlights</div>
            <div style={{ display: 'grid', gap: '8px' }}>
              {highlights.map((h, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ color: 'var(--text3)', fontSize: '12px', minWidth: '20px' }}>{i + 1}.</span>
                  <input style={{ ...inputStyle, flex: 1 }} value={h}
                    onChange={e => {
                      const next = [...highlights];
                      next[i] = e.target.value;
                      setHighlights(next);
                    }}
                    placeholder="z.B. Großvolumiges institutionelles Investment..." />
                  <button type="button" onClick={() => setHighlights(highlights.filter((_, j) => j !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: '16px', padding: '4px' }}>
                    ×
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => setHighlights([...highlights, ''])}
                style={{ padding: '6px 12px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '12px', cursor: 'pointer', color: 'var(--text2)', alignSelf: 'flex-start' }}>
                + Highlight hinzufügen
              </button>
            </div>

            <div style={sectionHead}>Investmentstory</div>
            <textarea style={textareaStyle}
              value={form.investment_story}
              onChange={e => set('investment_story', e.target.value)}
              placeholder="Warum dieses Asset investitionsrelevant ist..." />

            <div style={sectionHead}>Standort</div>
            <div>
              <label style={labelStyle}>Makrolage</label>
              <textarea style={textareaStyle}
                value={form.standort_makro}
                onChange={e => set('standort_makro', e.target.value)}
                placeholder="Wirtschaftlich relevante Region, Anbindung..." />
            </div>
            <div>
              <label style={labelStyle}>Mikrolage</label>
              <textarea style={textareaStyle}
                value={form.standort_mikro}
                onChange={e => set('standort_mikro', e.target.value)}
                placeholder="Direkte Umgebung, ÖPNV, Infrastruktur..." />
            </div>
            <div>
              <label style={labelStyle}>Marktrelevanz</label>
              <textarea style={textareaStyle}
                value={form.standort_markt}
                onChange={e => set('standort_markt', e.target.value)}
                placeholder="Lagequalität und Wettbewerbsfähigkeit..." />
            </div>

            <div style={sectionHead}>Weitere Texte</div>
            <div>
              <label style={labelStyle}>Gebäudebeschreibung</label>
              <textarea style={textareaStyle}
                value={form.gebaeude_beschreibung}
                onChange={e => set('gebaeude_beschreibung', e.target.value)}
                placeholder="Moderne Flächenqualität, flexible Grundrisse..." />
            </div>
            <div>
              <label style={labelStyle}>Mieterstruktur</label>
              <textarea style={textareaStyle}
                value={form.mieterstruktur_text}
                onChange={e => set('mieterstruktur_text', e.target.value)}
                placeholder="Diversifizierte Mieterstruktur, Branchenmix..." />
            </div>
            <div>
              <label style={labelStyle}>Ertragssituation</label>
              <textarea style={textareaStyle}
                value={form.ertragssituation_text}
                onChange={e => set('ertragssituation_text', e.target.value)}
                placeholder="Belastbarer Cashflow mit Entwicklungsspielraum..." />
            </div>
            <div>
              <label style={labelStyle}>Wertsteigerungspotenzial</label>
              <textarea style={textareaStyle}
                value={form.value_creation_text}
                onChange={e => set('value_creation_text', e.target.value)}
                placeholder="Mietanpassungen, Leerstandsvermietung, ESG..." />
            </div>
            <div>
              <label style={labelStyle}>ESG / Nachhaltigkeit</label>
              <textarea style={textareaStyle}
                value={form.esg_text}
                onChange={e => set('esg_text', e.target.value)}
                placeholder="Energieeffizienz, Gebäudebetrieb, ESG-Positionierung..." />
            </div>
          </>
        )}

        {/* ── TAB: PROZESS & KONTAKT ── */}
        {activeTab === 'prozess' && (
          <>
            <div style={sectionHead}>Verkaufsprozess</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Aktuelle Prozessphase</label>
                <select style={inputStyle} value={form.prozess_phase}
                  onChange={e => set('prozess_phase', e.target.value)}>
                  {PROZESS_PHASEN.map(p => <option key={p} value={p}>{p || '— wählen —'}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Verkaufsprozess Beschreibung</label>
              <textarea style={textareaStyle}
                value={form.verkaufsprozess_text}
                onChange={e => set('verkaufsprozess_text', e.target.value)}
                placeholder="Strukturierter, professionell geführter Bieterverfahren..." />
            </div>
            <div>
              <label style={labelStyle}>Vertraulichkeitshinweis / Disclaimer</label>
              <textarea style={{ ...textareaStyle, minHeight: '120px' }}
                value={form.vertraulichkeitshinweis}
                onChange={e => set('vertraulichkeitshinweis', e.target.value)}
                placeholder="Diese Unterlage wurde ausschließlich zu Informationszwecken erstellt..." />
            </div>

            <div style={{ ...sectionHead, marginTop: '8px' }}>Ansprechpartner</div>
            {teamMembers.length === 0 ? (
              <div style={{ padding: '16px', background: 'var(--surface2)', borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text3)' }}>
                Noch keine Team-Mitglieder angelegt. Bitte unter Einstellungen → Team erfassen.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '8px' }}>
                {teamMembers.map(tm => (
                  <label key={tm.id} style={{
                    display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
                    background: selectedTeam.includes(tm.id) ? 'var(--accent-light)' : 'var(--surface2)',
                    borderRadius: 'var(--radius)', cursor: 'pointer',
                    border: selectedTeam.includes(tm.id) ? '1px solid var(--accent)' : '1px solid var(--border)',
                  }}>
                    <input type="checkbox"
                      checked={selectedTeam.includes(tm.id)}
                      onChange={() => {
                        setSelectedTeam(prev =>
                          prev.includes(tm.id) ? prev.filter(id => id !== tm.id) : [...prev, tm.id]
                        );
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 500 }}>{tm.full_name}</div>
                      {tm.position && <div style={{ fontSize: '11px', color: 'var(--text3)' }}>{tm.position}</div>}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </>
        )}

        {/* Error + Buttons (immer sichtbar) */}
        {error && (
          <div style={{ padding: '10px 14px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 'var(--radius)', fontSize: '13px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '4px' }}>
          <button type="button" className="btn" onClick={onCancel} disabled={loading}>Abbrechen</button>
          <button type="submit" disabled={loading} style={{
            padding: '8px 20px', background: 'var(--accent)', color: '#fff',
            border: 'none', borderRadius: 'var(--radius)', fontSize: '13px',
            fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
          }}>
            {loading ? 'Wird gespeichert...' : isEdit ? 'Änderungen speichern' : 'Mandat anlegen'}
          </button>
        </div>
      </div>
    </form>
  );
}
